import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { PortalPayment } from "./models/PortalPayment";
import { portalSignOut } from "./portal-slice";
import { fetchPortalPayments, payPortalPayment } from "./slice-extensions/portal-money-slice-extension";

/** The Money list of one member (self or ward), page by page. */
export interface PortalPaymentsList {
  items: PortalPayment[];
  page: number;
  totalPages: number;
  status: "loading" | "succeeded" | "failed";
}

interface PortalMoneyState {
  paymentsByMemberId: Record<string, PortalPaymentsList>;
}

const initialState: PortalMoneyState = { paymentsByMemberId: {} };

const portalMoneySlice = createSlice({
  name: "portalMoney",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(portalSignOut, () => initialState)
      .addCase(fetchPortalPayments.pending, (state, action) => {
        const { memberId } = action.meta.arg;
        const current = state.paymentsByMemberId[memberId];
        state.paymentsByMemberId[memberId] = current
          ? { ...current, status: "loading" }
          : { items: [], page: 0, totalPages: 0, status: "loading" };
      })
      .addCase(fetchPortalPayments.fulfilled, (state, action) => {
        const { memberId, page, totalPages, items } = action.payload;
        const previous = page > 1 ? (state.paymentsByMemberId[memberId]?.items ?? []) : [];
        const seen = new Set(previous.map((p) => p.id));
        state.paymentsByMemberId[memberId] = {
          items: [...previous, ...items.filter((p) => !seen.has(p.id))],
          page,
          totalPages,
          status: "succeeded",
        };
      })
      .addCase(fetchPortalPayments.rejected, (state, action) => {
        const current = state.paymentsByMemberId[action.meta.arg.memberId];
        if (current) current.status = "failed";
      })
      .addCase(payPortalPayment.fulfilled, (state, action) => {
        const { memberId, paymentId, debit } = action.payload;
        const payment = state.paymentsByMemberId[memberId]?.items.find((p) => p.id === paymentId);
        if (payment) {
          payment.canPayNow = false;
          payment.pendingDebit = debit;
        }
      });
  },
});

export const selectPortalPayments = (memberId: string) => (state: RootState): PortalPaymentsList | undefined =>
  state.portalMoney.paymentsByMemberId[memberId];

export default portalMoneySlice.reducer;
